/* eslint-disable react/require-default-props */
import {
  Button,
  Cell,
  Grid,
  Loader,
  Panel,
  PanelBody,
  PanelCell,
  PanelFooter,
  PanelHeader,
  PanelRow,
} from '@teamsnap/teamsnap-ui';

import * as Sentry from '@sentry/browser';
import * as React from 'react';
import FormElementHook, { useFormDefaults } from './form-element-hook';
import MailingAddress from './mailing-address';
import OrgAdminFields from './org-admin-fields';
import { useForm } from 'react-hook-form';
import { isDecimal, validInteger } from '../../core/validation';
import { useMutation, useQuery } from '@apollo/client';

import * as UpdateOrganizationAdmin from '../../core/gql/mutations/update-organization-admin';
import * as UpdateOrganization from '../../core/gql/mutations/update-organization';
import * as CreateOrganization from '../../core/gql/mutations/create-organization';
import * as UpdateOrganizationSettings from '../../core/gql/mutations/update-organization-settings';
import * as AllOrganizations from '../../core/gql/queries/all-organizations';
import * as ShowOrganization from '../../core/gql/queries/show-organizations';
import * as ShowOrganizationSettings from '../../core/gql/queries/show-organization-settings';

import { toType } from '../../core/serialization';
import Organization from '../../models/organization';
import { reactiveFeedbackMessage } from '../../core/apollo-cache';
import { isOwner } from '../../core/utils';
import { Plans, PlansDisplayNames } from '../../enums';
import OrgLegacyUuidSection from './org-legacy-uuid-section';
import OrgEditLegacySeasonField from './org-edit-legacy-season-field';
import OrgEditAddtionalLegacySeasons from './org-edit-additional-legacy-section';
import OrgEditDisableFields from './org-edit-disable-section';
import { sportOptions } from './sport-select';
import OrgEditTournamentOrgId from './org-edit-tournament_org_id';
import {
  setChurnZeroContact,
  trackChurnZeroEvent,
} from 'components/common/useChurnZero';
import api from 'core/api';

const CreateElement = FormElementHook as new () => FormElementHook;

export interface Props {
  employeeView?: boolean;
  isModal?: boolean;
  selectedOrganizationId: number | null;
  onFinish?: (updated) => {};
}
export interface ProcessingFeeDefaults {
  creditCardBaseFeeAmountCents: number;
  creditCardRate: number;
  achBaseFeeAmountCents: number;
  achRate: number;
}

const OrganizationEditForm: React.FunctionComponent<Props> = ({
  isModal,
  employeeView,
  selectedOrganizationId,
  onFinish,
}) => {
  const showOrgQuery = employeeView
    ? ShowOrganization.ADMINS_QUERY
    : ShowOrganization.QUERY;

  const options = {
    refetchQueries: [
      { query: AllOrganizations.QUERY },
      {
        query: showOrgQuery,
        variables: { organizationId: selectedOrganizationId },
      },
    ],
  };

  const registrationTypeOptions = [
    { text: 'Reg 2.0 only', value: 'reg2' },
    { text: 'Reg 1.5 only', value: 'reg1' },
    { text: 'Reg 1.5 and Reg 2.0', value: 'both' },
  ];

  const [createOrganization, createOrganizationResponse] = useMutation(
    CreateOrganization.MUTATION,
    options
  );
  const [updateOrganization, updateOrganizationResponse] = useMutation(
    UpdateOrganization.MUTATION,
    options
  );

  const [updateOrganizationAsAdmin, updateOrganizationAsAdminResponse] =
    useMutation(UpdateOrganizationAdmin.MUTATION, options);

  const [updateOrganizationSettings, updateOrganizationSettingsResponse] =
    useMutation(UpdateOrganizationSettings.MUTATION, {
      refetchQueries: [
        {
          query: ShowOrganizationSettings.QUERY,
          variables: { organizationId: selectedOrganizationId },
        },
      ],
    });

  const getOrganization = useQuery(showOrgQuery, {
    variables: { organizationId: selectedOrganizationId },
    skip: !selectedOrganizationId,
  });

  const getOrganizationSettings = useQuery(ShowOrganizationSettings.QUERY, {
    variables: { organizationId: selectedOrganizationId },
    skip: !selectedOrganizationId,
  });

  const [currentOwner, setCurrentOwner] = React.useState(null);
  const [tsPersistentId, setTsPersistentId] = React.useState<boolean>(null);
  const [tournamentOrgId, setTournamentOrgId] = React.useState<boolean>(
    getOrganization.data?.showOrganization?.tournamentOrgId
  );
  const [addLegacySeasons, setAddLegacySeasons] = React.useState(
    getOrganization.data?.showOrganization?.hasLegacySeasonConnected
  );

  const [additionalLegacyOrgUuids, setAdditionalLegacyOrgUuids] =
    React.useState(
      getOrganization.data?.showOrganization?.additionalLegacyOrgs?.map(
        (stub) => stub.id
      )
    );
  const [processingFeeDefaults, setProcessingFeeDefaults] = React.useState<ProcessingFeeDefaults>(null);

  React.useEffect(() => {
    if (!employeeView) return;
    api.get<ProcessingFeeDefaults>('/processing_fee_defaults').then((response) => {
      setProcessingFeeDefaults(response.data);
    }).catch((e) => {
      Sentry.captureException(e);
      reactiveFeedbackMessage({
        message: `There was an error retrieving default rates: ${e.message}`,
        autoclose: true,
        isError: true,
      });
    });
  }, [employeeView]);


  const submit = async (values) => {
    const variables = {
      id: selectedOrganizationId,
      newOwnerUserId: values?.newOwnerUserId?.split('-')[0] || null,
      ownerEmail: values.ownerEmail,
      ownerFirstName: values.ownerFirstName,
      ownerLastName: values.ownerLastName,
      businessType: values.businessType,
      country: values.country,
      name: values.name,
      notes: values.notes,
      numberOfTeamsPaidFor: +values.numberOfTeamsPaidFor,
      paymentProvider: values.paymentProvider,
      registrationType: values.registrationType,
      plan: values.plan,
      resendInvite: values.resendInvite,
      sport: values.sport,
      timezone: values.timezone,
      zipCode: values.zipCode,
      mailingAddress: values.mailingAddress,
      mailingAddress2: values.mailingAddress2,
      mailingCity: values.mailingCity,
      mailingState: values.mailingState,
      mailingZip: values.mailingZip,
      mailingCountry: values.mailingCountry,
      playerCount: +values.playerCount,
      tsPersistentUuid: tsPersistentId,
      tournamentOrgId: tournamentOrgId,
      addLegacySeasons: addLegacySeasons,
      additionalLegacyOrgUuids: additionalLegacyOrgUuids,
      disabled: values.disabled,
      disabledMessageType: values.disabledMessageType,
      disabledMessageTemplate: values.disabledMessageTemplate,
      isTest: values.isTest,
      creditCardProcessingFeeBase: +values.creditCardProcessingFeeBase * 100,
      creditCardProcessingFeeRate: +values.creditCardProcessingFeeRate / 100,
      achProcessingFeeBase: +values.achProcessingFeeBase * 100,
      achProcessingFeeRate: +values.achProcessingFeeRate / 100,
      updateAllSeasonsAddress: values.updateAllSeasonsAddress,
      websiteUrl: values.websiteUrl,
    };

    try {
      if (selectedOrganizationId) {
        if (employeeView) {
          const org = getOrganization.data?.showOrganization;
          if (variables.newOwnerUserId && !org.isTest && !org.isTrial) {
            setChurnZeroContact(values.newOwnerUserId.split('-')[0], org);
            trackChurnZeroEvent('TSB - League Owner Changed', org);
          }
          await updateOrganizationAsAdmin({ variables });
          await updateOrganizationSettings({ variables });
        } else {
          await updateOrganization({ variables });
        }

        reactiveFeedbackMessage({
          message: 'Successfully updated organization settings!',
          autoclose: true,
          isError: false,
        });
      } else {
        const createdOrg = await createOrganization({ variables });

        if (
          employeeView &&
          (variables.creditCardProcessingFeeBase ||
            variables.creditCardProcessingFeeRate ||
            variables.achProcessingFeeBase ||
            variables.achProcessingFeeRate)
        ) {
          variables.id = createdOrg.data?.createOrganization?.id;
          await updateOrganizationSettings({ variables });
        }

        reactiveFeedbackMessage({
          message: `The org ${values.name} has been created.`,
          autoclose: true,
          isError: false,
        });
      }

      hide(true);
    } catch (e) {
      const errorMessage = selectedOrganizationId
        ? 'Something went wrong while trying to update your organization. Please try again!'
        : 'Something went wrong while trying to create your organization. Please try again!';

      reactiveFeedbackMessage({
        message: errorMessage,
        autoclose: true,
        isError: true,
      });

      console.warn('Unable to validate/submit organization form.', e);
    }
  };

  const hide = (updated?: boolean) => {
    onFinish?.(updated);
  };

  const organizationForm = useForm();

  const convertRate = (rate) => {
    if (Number(rate) === 0) { return rate }
    return rate ? (rate * 100).toFixed(2) : null;
  };

  const convertCents = (cents) => {
    if (Number(cents) === 0) { return cents }
    return cents ? (cents / 100).toFixed(2) : null;
  };

  const setupFormDefaults = React.useCallback((data) => {
    const organization = toType(data?.showOrganization, Organization);
    const invite = organization.ownerInvite || {
      id: null,
      firstName: null,
      lastName: null,
      email: null,
      accepted: null,
    };

    let orgSettingsField = {};
    if (employeeView && data.showOrganizationSettings) {
      const {
        achProcessingFeeBase,
        achProcessingFeeRate,
        creditCardProcessingFeeBase,
        creditCardProcessingFeeRate
      } = data.showOrganizationSettings;

      orgSettingsField = {
        achProcessingFeeBase: convertCents(achProcessingFeeBase),
        achProcessingFeeRate: convertRate(achProcessingFeeRate),
        creditCardProcessingFeeBase: convertCents(creditCardProcessingFeeBase),
        creditCardProcessingFeeRate: convertRate(creditCardProcessingFeeRate),
      };
    } else {
      orgSettingsField = {
        achProcessingFeeBase: convertCents(data?.achBaseFeeAmountCents),
        achProcessingFeeRate: convertRate(data?.achRate),
        creditCardProcessingFeeBase: convertCents(data?.creditCardBaseFeeAmountCents),
        creditCardProcessingFeeRate: convertRate(data?.creditCardRate),
      };
    }

    Object.entries({
      name: organization.name,
      country: organization.country,
      zipCode: organization.zipCode,
      timezone: organization.timezone,
      sport: organization.sport,
      mailingAddress: organization.mailingAddress,
      mailingAddress2: organization.mailingAddress2,
      mailingCity: organization.mailingCity,
      mailingState: organization.mailingState,
      mailingCountry: organization.mailingCountry,
      mailingZip: organization.mailingZip,
      ownerFirstName: invite.firstName,
      ownerLastName: invite.lastName,
      ownerEmail: invite.email,
      inviteAccepted: invite.accepted,
      businessType: organization.businessType,
      plan: organization.plan || 14,
      paymentProvider: organization.paymentProvider || 'stripe',
      registrationType: organization.registrationType || '',
      numberOfTeamsPaidFor: organization.numberOfTeamsPaidFor,
      notes: organization.notes,
      roles: organization.roles,
      playerCount: organization.playerCount,
      disabled: organization.disabled,
      disabledMessageType: organization.disabledMessageType,
      disabledMessageTemplate: organization.disabledMessageTemplate,
      isTest: organization.isTest,
      tournamentOrgId: organization.tournamentOrgId,
      websiteUrl: organization.websiteUrl,
      ...orgSettingsField,
    }).forEach(([key, value]) => {
      organizationForm.setValue(key, value);
    });

    const owner = organization.staff?.find(isOwner);
    setCurrentOwner(owner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSubmitting =
    createOrganizationResponse.loading ||
    updateOrganizationResponse.loading ||
    updateOrganizationAsAdminResponse.loading ||
    updateOrganizationSettingsResponse.loading;

  useFormDefaults(
    { ...getOrganization.data, ...getOrganizationSettings.data, ...processingFeeDefaults },
    setupFormDefaults,
    [
      getOrganization.called,
      getOrganization.loading,
      getOrganizationSettings.called,
      getOrganizationSettings.loading,
      processingFeeDefaults,
    ]
  );

  const { handleSubmit, register } = organizationForm;

  const prepopulateWithFoundData = (clubData) => {
    Object.entries({
      name: clubData.name,
      ownerFirstName: clubData.ownerFirstName,
      ownerLastName: clubData.ownerLastName,
      ownerEmail: clubData.ownerEmail,
      country: clubData.country,
      businessType: clubData.businessType,
      plan: clubData.plan || 14,
      paymentProvider: clubData.paymentProvider || 'stripe',
      mailingAddress: clubData.mailingAddress,
      numberOfTeamsPaidFor: clubData.numberOfTeamsPaidFor,
      zipCode: clubData.zipCode,
      timezone: clubData.timezone,
      sport: clubData.sport,
    }).forEach(([key, value]) => {
      organizationForm.setValue(key, value);
    });
  };

  const getPlanOptions = () =>
    Object.keys(Plans)
      .filter((key) => isNaN(Number(key))) // Filter out numeric values
      .map((key) => ({
        text: PlansDisplayNames[key as keyof typeof PlansDisplayNames].toString(),
        value: Plans[key as keyof typeof Plans].toString(),
      }));

  return (
    <form
      className={isModal ? '' : 'u-maxWidthMd'}
      onSubmit={handleSubmit(submit)}
    >
      {!getOrganization.loading && (
        <div id="organization-create-editor-content" className="page-content">
          <Panel mods="Panel--form">
            <PanelHeader title="Organization Details" />
            <PanelBody>
              {employeeView && !selectedOrganizationId ? (
                <OrgLegacyUuidSection
                  uuid={getOrganization.data?.showOrganization?.uuid}
                  onPopulate={prepopulateWithFoundData}
                  setTsPersistentId={setTsPersistentId}
                  setAddLegacySeasons={setAddLegacySeasons}
                  addLegacySeasons={addLegacySeasons}
                />
              ) : null}

              {employeeView &&
                selectedOrganizationId &&
                getOrganization.data?.showOrganization?.legacyOrg ? (
                <OrgEditLegacySeasonField
                  legacyOrg={getOrganization.data?.showOrganization?.legacyOrg}
                  setAddLegacySeasons={setAddLegacySeasons}
                  addLegacySeasons={addLegacySeasons}
                  hasLegacySeasonConnected={
                    getOrganization.data?.showOrganization
                      ?.hasLegacySeasonConnected
                  }
                />
              ) : null}

              {employeeView &&
                (!selectedOrganizationId ||
                  getOrganization.data?.showOrganization?.legacyOrg) ? (
                <OrgEditAddtionalLegacySeasons
                  additionalLegacyOrgs={
                    getOrganization.data?.showOrganization?.additionalLegacyOrgs
                  }
                  setAdditionalLegacyOrgUuids={setAdditionalLegacyOrgUuids}
                  mainLegacyUuid={
                    addLegacySeasons ||
                    getOrganization.data?.showOrganization
                      ?.hasLegacySeasonConnected
                  }
                />
              ) : null}

              {employeeView && (
                <OrgEditTournamentOrgId
                  currentTournamentOrg={
                    getOrganization.data?.showOrganization?.tournamentOrg
                  }
                  setTournamentOrgId={setTournamentOrgId}
                />
              )}

              <PanelRow isWithCells={true} mods="Panel-row--header">
                <PanelCell mods="Panel-cell--header u-size3of12 u-flex">
                  <h4>Name</h4>
                </PanelCell>

                <PanelCell mods="u-size9of12">
                  <CreateElement
                    inputRef={register({
                      required: 'You must enter a name for the organization.',
                    })}
                    useHookResponse={organizationForm}
                    type="text"
                    field="name"
                    label="Organization Name"
                    labelHidden={true}
                    placeholder="Organization name"
                  />
                </PanelCell>
              </PanelRow>

              {employeeView && (
                <OrgAdminFields
                  selectedOrganizationId={selectedOrganizationId}
                  useHookResponse={organizationForm}
                  currentOwner={currentOwner}
                />
              )}

              <PanelRow isWithCells={true} mods="Panel-row--header">
                <PanelCell mods="Panel-cell--header u-size3of12 u-flex">
                  <h4>Location</h4>
                </PanelCell>

                <PanelCell mods="u-size9of12">
                  <Grid isWithGutter={true}>
                    <Cell mods="u-size6of12">
                      <CreateElement
                        useHookResponse={organizationForm}
                        inputRef={register({
                          required: 'Can\'t be blank.',
                        })}
                        type="country"
                        field="country"
                        placeholder="Country"
                        label={
                          <div>
                            Country
                            <div className="u-textNormal u-fontSizeSm">
                              Determines default currency for payments
                            </div>
                          </div>
                        }
                      />
                    </Cell>

                    <Cell mods="u-size6of12">
                      <CreateElement
                        useHookResponse={organizationForm}
                        inputRef={register({
                          required: 'Can\'t be blank.',
                        })}
                        type="text"
                        field="zipCode"
                        placeholder="Zip/Postal Code"
                        label={
                          <div>
                            Zip/Postal Code{' '}
                            <div className="u-textNormal u-fontSizeSm">
                              For local weather forcasts
                            </div>
                          </div>
                        }
                      />
                    </Cell>
                  </Grid>

                  <br />

                  <Cell mods="u-size12of12">
                    <CreateElement
                      useHookResponse={organizationForm}
                      inputRef={register({
                        required: 'Can\'t be blank.',
                      })}
                      type="timezone"
                      field="timezone"
                      placeholder="Time Zone"
                      label="Time Zone"
                    />
                  </Cell>
                </PanelCell>
              </PanelRow>

              <PanelRow isWithCells={true} mods="Panel-row--header">
                <PanelCell mods="Panel-cell--header u-size3of12 u-flex">
                  <h4>Sport</h4>
                </PanelCell>

                <PanelCell mods="u-size9of12">
                  <Cell mods="u-size12of12">
                    <CreateElement
                      useHookResponse={organizationForm}
                      inputRef={register({
                        required: 'Can\'t be blank.',
                      })}
                      type="select"
                      field="sport"
                      label="sport"
                      labelHidden={true}
                      placeholder="Sport"
                      options={[
                        { value: 'multi_sport', text: 'Multi Sport' },
                        ...sportOptions,
                      ].sort((a, b) =>
                        a.text > b.text ? 1 : b.text > a.text ? -1 : 0
                      )}
                    />
                  </Cell>
                </PanelCell>
              </PanelRow>

              <MailingAddress useHookResponse={organizationForm} />

              {employeeView && (
                <span>
                  <PanelRow isWithCells={true} mods="Panel-row--header">
                    <PanelCell mods="Panel-cell--header u-size3of12 u-flex">
                      <h4>Business Type</h4>
                    </PanelCell>

                    <PanelCell mods="u-size9of12">
                      <Cell mods="u-size12of12">
                        <CreateElement
                          useHookResponse={organizationForm}
                          inputRef={register({
                            required: 'Can\'t be blank.',
                          })}
                          type="select"
                          field="businessType"
                          label="Business type"
                          labelHidden={true}
                          options={[
                            { text: 'Select a business type', value: '' },
                            { text: 'Non-Profit', value: 'non-profit' },
                            { text: 'Individual', value: 'individual' },
                            { text: 'Corporation', value: 'corporation' },
                          ]}
                        />
                      </Cell>
                    </PanelCell>
                  </PanelRow>

                  <PanelRow isWithCells={true} mods="Panel-row--header">
                    <PanelCell mods="Panel-cell--header u-size3of12 u-flex">
                      <h4>Plan</h4>
                    </PanelCell>

                    <PanelCell mods="u-size9of12">
                      <Cell mods="u-size12of12">
                        <CreateElement
                          useHookResponse={organizationForm}
                          inputRef={register({
                            required: 'Can\'t be blank.',
                          })}
                          type="select"
                          field="plan"
                          label="Plan"
                          labelHidden={true}
                          options={getPlanOptions()}
                        />
                      </Cell>
                    </PanelCell>
                  </PanelRow>

                  <PanelRow isWithCells={true} mods="Panel-row--header">
                    <PanelCell mods="Panel-cell--header u-size3of12 u-flex">
                      <h4>Registration Version</h4>
                    </PanelCell>

                    <PanelCell mods="u-size9of12">
                      <Cell mods="u-size12of12">
                        <CreateElement
                          useHookResponse={organizationForm}
                          inputRef={register({
                            required: 'Can\'t be blank.',
                          })}
                          type="select"
                          field="registrationType"
                          label="Registration version"
                          labelHidden={true}
                          placeholder="Choose one"
                          options={registrationTypeOptions}
                        />
                      </Cell>
                    </PanelCell>
                  </PanelRow>

                  <PanelRow isWithCells={true} mods="Panel-row--header">
                    <PanelCell mods="Panel-cell--header u-size3of12 u-flex">
                      <h4>Payment Provider</h4>
                    </PanelCell>

                    <PanelCell mods="u-size9of12">
                      <Cell mods="u-size12of12">
                        <CreateElement
                          useHookResponse={organizationForm}
                          inputRef={register({
                            required: 'Can\'t be blank.',
                          })}
                          type="select"
                          field="paymentProvider"
                          label="Payment provider"
                          labelHidden={true}
                          options={[{ text: 'Stripe', value: 'stripe' }]}
                        />
                      </Cell>
                    </PanelCell>
                  </PanelRow>

                  <PanelRow isWithCells={true} mods="Panel-row--header">
                    <PanelCell mods="Panel-cell--header u-size3of12 u-flex">
                      <h4>Custom Rates</h4>
                    </PanelCell>

                    <PanelCell mods="u-size9of12">
                      <Grid isWithGutter={true} isAlignMiddle>
                        <Cell mods="u-spaceBottomMd">
                          <CreateElement
                            useHookResponse={organizationForm}
                            inputRef={register({
                              maxLength: 4,
                              validate: (value) =>
                                isDecimal(value) ||
                                validInteger(value) ||
                                'Please input a valid value',
                            })}
                            type="number"
                            field="creditCardProcessingFeeRate"
                            isInline={true}
                            label={`New Credit Card Processing Rate (${convertRate(processingFeeDefaults?.creditCardRate)}%)`}
                          />
                        </Cell>
                      </Grid>

                      <Grid isWithGutter={true} isAlignMiddle>
                        <Cell mods="u-spaceBottomMd">
                          <CreateElement
                            useHookResponse={organizationForm}
                            inputRef={register({
                              maxLength: 4,
                              validate: (value) =>
                                isDecimal(value) ||
                                validInteger(value) ||
                                'Please input a valid value',
                            })}
                            type="number"
                            field="creditCardProcessingFeeBase"
                            isInline={true}
                            label={`New Credit Card Online Fee (${convertCents(processingFeeDefaults?.creditCardBaseFeeAmountCents)})`}
                          />
                        </Cell>
                      </Grid>

                      <Grid isWithGutter={true} isAlignMiddle>
                        <Cell mods="u-spaceBottomMd">
                          <CreateElement
                            useHookResponse={organizationForm}
                            inputRef={register({
                              maxLength: 4,
                              validate: (value) =>
                                isDecimal(value) ||
                                validInteger(value) ||
                                'Please input a valid value',
                            })}
                            type="number"
                            field="achProcessingFeeRate"
                            isInline={true}
                            label={`New ACH Processing Rate (${convertRate(processingFeeDefaults?.achRate)}%)`}
                          />
                        </Cell>
                      </Grid>

                      <Grid isWithGutter={true} isAlignMiddle>
                        <Cell>
                          <CreateElement
                            useHookResponse={organizationForm}
                            inputRef={register({
                              maxLength: 4,
                              validate: (value) =>
                                isDecimal(value) ||
                                validInteger(value) ||
                                'Please input a valid value',
                            })}
                            type="number"
                            field="achProcessingFeeBase"
                            isInline={true}
                            label={`New ACH Online Fee (${convertCents(processingFeeDefaults?.achBaseFeeAmountCents)})`}
                          />
                        </Cell>
                      </Grid>
                    </PanelCell>
                  </PanelRow>

                  <PanelRow isWithCells={true} mods="Panel-row--header">
                    <PanelCell mods="Panel-cell--header u-size3of12 u-flex">
                      <h4>Plan Paid for This Number of Teams</h4>
                    </PanelCell>

                    <PanelCell mods="u-size9of12">
                      <Cell mods="u-size12of12">
                        <CreateElement
                          useHookResponse={organizationForm}
                          inputRef={register({
                            required: false,
                            validate: (value) =>
                              !value ||
                              validInteger(value) ||
                              'Please input a valid integer',
                          })}
                          type="text"
                          label="Number of teams paid for"
                          labelHidden={true}
                          field="numberOfTeamsPaidFor"
                        />
                      </Cell>
                    </PanelCell>
                  </PanelRow>

                  <PanelRow isWithCells={true} mods="Panel-row--header">
                    <PanelCell mods="Panel-cell--header u-size3of12 u-flex">
                      <h4>Player Count</h4>
                    </PanelCell>

                    <PanelCell mods="u-size9of12">
                      <Cell mods="u-size12of12">
                        <CreateElement
                          useHookResponse={organizationForm}
                          inputRef={register({
                            required: 'Can\'t be blank.',
                            maxLength: 4,
                            validate: (value) =>
                              !value ||
                              validInteger(value) ||
                              'Please input a valid integer',
                          })}
                          type="number"
                          label="Player Count"
                          labelHidden={true}
                          field="playerCount"
                        />
                      </Cell>
                    </PanelCell>
                  </PanelRow>

                  <PanelRow isWithCells={true} mods="Panel-row--header">
                    <PanelCell mods="Panel-cell--header u-size3of12 u-flex">
                      <h4>Notes</h4>
                    </PanelCell>

                    <PanelCell mods="u-size9of12">
                      <CreateElement
                        useHookResponse={organizationForm}
                        inputRef={register({
                          required: false,
                        })}
                        type="textarea"
                        field="notes"
                        label="Notes"
                        labelHidden={true}
                      />
                    </PanelCell>
                  </PanelRow>
                </span>
              )}

              {employeeView &&
                selectedOrganizationId &&
                getOrganization.data?.showOrganization ? (
                <OrgEditDisableFields
                  organization={getOrganization.data?.showOrganization}
                  useHookResponse={organizationForm}
                />
              ) : null}

              {employeeView && (
                <>
                  <PanelRow isWithCells={true} mods="Panel-row--header">
                    <PanelCell mods="Panel-cell--header u-size3of12 u-flex">
                      <h4>This is an Internal Test Org</h4>
                    </PanelCell>

                    <PanelCell mods="u-size9of12">
                      <Grid isWithGutter={true}>
                        <Cell mods="u-size12of12 u-colorWarning">
                          <div className="u-spaceBottomMd">
                            Test Organization (exclude from data analytics)
                          </div>
                        </Cell>
                      </Grid>

                      <Grid isWithGutter={true}>
                        <Cell mods="u-size12of12">
                          <CreateElement
                            useHookResponse={organizationForm}
                            field="isTest"
                            inputRef={register({
                              required: false,
                            })}
                            type="checkbox"
                            label="Test Organization"
                            isInline={true}
                          />
                        </Cell>
                      </Grid>
                    </PanelCell>
                  </PanelRow>

                  <PanelRow isWithCells={true} mods="Panel-row--header">
                    <PanelCell mods="Panel-cell--header u-size3of12 u-flex">
                      <h4>Website URL</h4>
                    </PanelCell>

                    <PanelCell mods="u-size9of12">
                      <CreateElement
                        inputRef={register({
                          validate: {
                            value: (value) => {
                              const pattern =
                                /^(http(s)?:\/\/)[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/gm;
                              if (value && !pattern.test(value)) {
                                return 'Please enter a valid URL.';
                              }

                              return true;
                            },
                          },
                        })}
                        useHookResponse={organizationForm}
                        type="text"
                        field="websiteUrl"
                        label="Website URL"
                        labelHidden={true}
                        placeholder="Website URL"
                      />
                    </PanelCell>
                  </PanelRow>
                </>
              )}
            </PanelBody>

            <PanelFooter mods="u-textLeft">
              <div className="button-list">
                {isModal ? (
                  <Button mods="Button--negative" icon="dismiss" onClick={hide}>
                    Cancel
                  </Button>
                ) : null}

                <Button
                  isDisabled={isSubmitting}
                  color="primary"
                  icon={isSubmitting ? null : 'check'}
                  type="submit"
                >
                  {isSubmitting && (
                    <Loader
                      type="spin"
                      style={{ width: '13px', height: '13px' }}
                    />
                  )}
                  {selectedOrganizationId ? 'Update' : 'Create'} Organization
                </Button>
              </div>
            </PanelFooter>
          </Panel>
        </div>
      )}
    </form>
  );
};

export default OrganizationEditForm;
